/**
 * Manage creation and control of <video> element
 * @see https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Using_HTML5_audio_and_video
 */
export class VideoPlayerHtml5 {
	/**
	 * @param {Element} element  The element in which to render the <video> element
	 */
	constructor(element, modalService = null) {
		/**
		 * The element in which to render the <video> element
		 * @type {Element} element
		 */
		this.element = element;
		/**
		 * The type of player "html5"
		 * @type {String} type
		 */
		this.type = 'html5';
		/**
		 * The current volume where 0 is muted and 1 is full volume
		 * @type {Number} volume
		 */
		this.volume = 1;
		/**
		 * Reference to a ModalService instance
		 */
		this.modalService = modalService;
	}

	/**
	 * Play the given URL
	 * @param {String} url  The full URL to the video file to play
	 * @returns {Promise}  The returned promise will resolve when the video is done playing.
	 * It will be rejected if there is an error loading or playing the video.
	 */
	play(url) {
		return new Promise((resolve, reject) => {
			/** @private */
			this.url = url;
			// first set calbacks to noop so that the promise won't resolve
			// when we call .stop() below
			this._onVideoEnded = resolve;
			this._onVideoError = reject;
			if (this.video) {
				// console.log('reappending video');
				this.video.src = url;
				this.element.appendChild(this.video);
				this.video.play();
			} else {
				this._initPlayer();
			}
		});
	}

	/**
	 * Suspend playback
	 */
	pause() {
		if (this.video) {
			this.video.pause();
		}
	}

	/**
	 * Stop playback and remove <video> element
	 */
	stop() {
		this.pause();
		this._cleanup();
	}

	/**
	 * Update the volume. May be called before or after `play()` is called.
	 * @param {Number} volume  A number between 0 and 1 where 0 is mute and 1 is full volume
	 */
	setVolume(volume) {
		// console.log('HTML5 setVolume attempt', volume, !!this.video);
		if (typeof volume == 'number' && volume >= 0 && volume <= 1) {
			this.volume = volume;
		}
		if (this.video) {
			this.video.volume = this.volume;
		}
	}

	/**
	 * Check if the given URL is a playable attachment file
	 * @param {String} url
	 * @returns {Boolean}  True if playable
	 */
	static isPlayable(url) {
		return /^(http:\/|https:\/|\/|)\/.+\.(mp4|m4v)($|\?|#)/i.test(url);
	}

	/**
	 * Create the <video> element and append
	 * @private
	 */
	_initPlayer() {
		/** @private */
		this.video = document.createElement('video');
		this.video.setAttribute('width', '100%');
		this.video.setAttribute('height', '100%');
		this.video.setAttribute('controls', '');
		this.video.setAttribute('autoplay', '');
		this.video.setAttribute('src', this.url);
		this.video.addEventListener('ended', () => this._onVideoEnded(), false);
		this.video.addEventListener('error', () => this._onVideoError(), false);
		this.video.addEventListener('webkitendfullscreen', () => this._closeModal(), false);
		this.video.textContent = 'This browser does not support streaming video.';
		this.video.style.objectFit = 'contain';
		this.video.style.backgroundColor = 'black';
		// console.log('html5 appending video element', typeof this.volume);
		this.element.appendChild(this.video);
		if (typeof this.volume == 'number') {
			this.setVolume(this.volume);
		}
	}

	/**
	 * Close
	 * @private
	 */
	_closeModal() {
		this.modalService.closeTop();
	}

	/**
	 * Remove the <video> element
	 * @private
	 */
	_cleanup() {
		this.element.removeChild(this.video);
	}
}
